import { useState, useCallback } from 'react';
import {
  useInvitationCodes,
  INVITATION_CODE_STATUS_LABELS,
  INVITATION_CODE_SOURCE_LABELS,
  ALL_INVITATION_CODE_STATUSES,
} from '../features/invitation';
import type { InvitationCodeStatus, InvitationCodeSource } from '../features/invitation';
import { CreateCodesDialog } from '../components/feedback/CreateCodesDialog';
import { AssignCodeDialog } from '../components/feedback/AssignCodeDialog';

const STATUS_STYLES: Record<InvitationCodeStatus, string> = {
  available: 'bg-green-50 text-green-700',
  assigned: 'bg-blue-50 text-blue-700',
  disabled: 'bg-gray-100 text-gray-500',
};

function formatDateTime(iso: string | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function InvitationCodesPage() {
  const {
    items, total, page, totalPages,
    status, loading, error, submitting,
    setPage, setStatus, resetFilters, reload,
    createCodes, assignCode,
  } = useInvitationCodes();

  const [showCreate, setShowCreate] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  const handleCreate = useCallback((codes: string[], generateCount: number, note: string) => {
    createCodes(codes, generateCount, note);
    setShowCreate(false);
  }, [createCodes]);

  const handleAssign = useCallback((userId: number, inviteCode: string, note: string) => {
    assignCode(userId, inviteCode, note);
    setShowAssign(false);
  }, [assignCode]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">管理邀请码库存，支持手工创建、批量生成和分配给用户。</p>

      {/* Filters + Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded-md border border-gray-300 px-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">全部状态</option>
            {ALL_INVITATION_CODE_STATUSES.map((s) => (
              <option key={s} value={s}>{INVITATION_CODE_STATUS_LABELS[s]}</option>
            ))}
          </select>
          <button
            onClick={resetFilters}
            className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            重置
          </button>
          <button
            onClick={reload}
            className="h-9 px-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            刷新
          </button>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreate(true)}
            className="h-9 px-4 text-sm bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
          >
            创建邀请码
          </button>
          <button
            onClick={() => setShowAssign(true)}
            className="h-9 px-4 text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
          >
            分配邀请码
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="py-10 text-center">
          <p className="text-sm text-red-500 mb-3">{error}</p>
          <button onClick={reload} className="text-sm text-primary hover:underline">重试</button>
        </div>
      )}

      {/* Loading */}
      {loading && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
        </div>
      )}

      {/* Table */}
      {!loading && !error && (
        <>
          {items.length === 0 ? (
            <div className="py-20 text-center text-sm text-gray-400">暂无数据</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">邀请码</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">状态</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">来源</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">备注</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">分配用户</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">创建者</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">创建时间</th>
                    <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">更新时间</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.code} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-900 font-mono tabular-nums whitespace-nowrap">{item.code}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[item.status as InvitationCodeStatus] ?? 'bg-gray-100 text-gray-600'}`}>
                          {INVITATION_CODE_STATUS_LABELS[item.status as InvitationCodeStatus] ?? item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs whitespace-nowrap">
                        {INVITATION_CODE_SOURCE_LABELS[item.source as InvitationCodeSource] ?? item.source}
                      </td>
                      <td className="px-4 py-3 text-gray-500 max-w-[160px] truncate text-xs" title={item.note || '-'}>
                        {item.note || '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs tabular-nums">
                        {item.assigned_user_id ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 text-xs tabular-nums">
                        {item.created_by ?? '-'}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.created_at)}
                      </td>
                      <td className="px-4 py-3 text-center text-gray-500 whitespace-nowrap text-xs">
                        {formatDateTime(item.updated_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          <div className="flex items-center justify-between pt-2 text-sm text-gray-500">
            <span>共 {total} 条</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                上一页
              </button>
              <span className="text-sm tabular-nums">{page} / {totalPages}</span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-gray-300 text-sm disabled:opacity-40 hover:bg-gray-50 transition-colors"
              >
                下一页
              </button>
            </div>
          </div>
        </>
      )}

      {/* Dialogs */}
      <CreateCodesDialog
        open={showCreate}
        loading={submitting}
        onConfirm={handleCreate}
        onCancel={() => setShowCreate(false)}
      />
      <AssignCodeDialog
        open={showAssign}
        loading={submitting}
        onConfirm={handleAssign}
        onCancel={() => setShowAssign(false)}
      />
    </div>
  );
}
